export const EMBEDDING_IN_PROCESS = "in_process";
export const EMBEDDING_FAILED = "failed";
export const EMBEDDING_READY = "succeeded";

export const EMBEDDING_STATUS_TOOLTIPS = {
  [EMBEDDING_READY]: "Available.",
  [EMBEDDING_IN_PROCESS]: "Document link is in progress.",
  [EMBEDDING_FAILED]:
    "Failed to link to Unicli. To learn more or to try linking again, go to the Unicli Portal.",
};

export const getEmbeddingStatusTooltip = (embeddingStatus) =>
  EMBEDDING_STATUS_TOOLTIPS[embeddingStatus] || "";

export const MAX_SELECTED_FILES = 4;
export const RESOURCE_POLL_INTERVAL_MS = 30000;
export const MAX_RESOURCE_POLL_ATTEMPTS = 12;

export const IN_PROCESS_MESSAGE =
  "One or more of the requested files is still being processed. Remove the file from context to proceed without it, or wait for processing to complete.";

export const FAILED_MESSAGE_PREFIX =
  "The following files were not linked to the AI service, please login to the Unicli Portal to view the error and re-link it, or remove the file from context to proceed: ";

export const POLLING_TIMEOUT_MESSAGE =
  "File processing is still not complete. Please wait a few minutes, remove the files, or try sending again.";

const ROOT_PATH_MARKER = "root:/";

export const extractSharePointRootPath = (path) => {
  if (!path || typeof path !== "string") {
    return "";
  }
  const markerIndex = path.indexOf(ROOT_PATH_MARKER);
  if (markerIndex === -1) {
    return "";
  }
  return path.slice(markerIndex + ROOT_PATH_MARKER.length).trim();
};

export const getResourceParentPath = (resource) =>
  extractSharePointRootPath(resource?.parent_json?.path);

export const getFolderParentPath = (folder) => extractSharePointRootPath(folder?.parent_path);

const compareByParentPathThenName = (left, right) => {
  const pathCompare = (left.parentPath || "").localeCompare(right.parentPath || "", undefined, {
    sensitivity: "base",
  });
  if (pathCompare !== 0) {
    return pathCompare;
  }
  return (left.name || "").localeCompare(right.name || "", undefined, { sensitivity: "base" });
};

export const sortByParentPathThenName = (items) => [...items].sort(compareByParentPathThenName);

export const normalizeResource = (resource) => ({
  connected_id: resource.connected_id,
  name: resource.name || "Unnamed file",
  embedding_status: resource.embedding_status,
  resource_uri: resource.resource_uri || resource.url || null,
  url: resource.url || resource.resource_uri || null,
  parentPath: getResourceParentPath(resource),
});

export const normalizeFolder = (folder) => ({
  id: folder.id,
  name: folder.name || "Unnamed folder",
  parentPath: getFolderParentPath(folder),
});

export const buildResourceReference = (resource) => ({
  connected_id: resource.connected_id,
  name: resource.name,
  url: resource.resource_uri || resource.url || null,
});

export const getResourceViewUrl = (resource) => resource.resource_uri || resource.url || null;

export const syncSelectedWithAvailable = (selectedFiles, availableResources) => {
  if (!selectedFiles.length || !availableResources.length) {
    return selectedFiles;
  }
  const byId = new Map(availableResources.map((r) => [r.connected_id, r]));
  return selectedFiles.map((file) => {
    const latest = byId.get(file.connected_id);
    return latest ? normalizeResource(latest) : file;
  });
};

export const pruneStaleSelections = (selectedFiles, availableResources) => {
  if (!availableResources.length) {
    return selectedFiles;
  }
  const ids = new Set(availableResources.map((r) => r.connected_id));
  return selectedFiles.filter((file) => ids.has(file.connected_id));
};

export const getFailedFiles = (selectedFiles) =>
  selectedFiles.filter((file) => file.embedding_status === EMBEDDING_FAILED);

export const hasInProcessFiles = (selectedFiles) =>
  selectedFiles.some((file) => file.embedding_status === EMBEDDING_IN_PROCESS);

export const filterResourcesBySearch = (resources, searchTerm, selectedIds) => {
  const term = searchTerm.trim().toLowerCase();
  return resources.filter((resource) => {
    if (selectedIds.has(resource.connected_id)) {
      return false;
    }
    if (!term) {
      return true;
    }
    return (resource.name || "").toLowerCase().includes(term);
  });
};
